import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { getFormattedDate } from "./dashboard/utils/getFormattedDate";
import { getCommentAuthorDisplayName } from "../utils/contributorDisplayName";
import "./CommentItem.css";

interface Author {
  _id?: string;
  firstName?: string;
  secondName?: string;
  surname?: string;
  email?: string;
}

interface CommentShape {
  _id: string;
  author: Author | string;
  authorDisplayName?: string;
  text: string;
  createdAt: string;
}

interface Props {
  comment: CommentShape;
  canDelete: boolean;
  canEdit: boolean;
  onDelete: (commentId: string) => Promise<void>;
  onEdit: (commentId: string, text: string) => Promise<void>;
}

const CommentItem: React.FC<Props> = ({
  comment,
  canDelete,
  canEdit,
  onDelete,
  onEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [text, setText] = useState(comment.text);
  const containerRef = useRef<HTMLElement>(null);

  const authorName = getCommentAuthorDisplayName(
    comment.author,
    comment.authorDisplayName,
  );
  const showMenu = (canEdit || canDelete) && !isEditing;

  useEffect(() => {
    setText(comment.text);
  }, [comment.text]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    await onEdit(comment._id, trimmed);
    setIsEditing(false);
    setIsMenuOpen(false);
  };

  const handleCancelEdit = () => {
    setText(comment.text);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this comment?")) return;
    setIsMenuOpen(false);
    await onDelete(comment._id);
  };

  return (
    <article className="publication-comment" ref={containerRef}>
      <header className="publication-comment__header">
        <div className="publication-comment__meta">
          <span className="publication-comment__author">{authorName}</span>
          <time
            className="publication-comment__date"
            dateTime={comment.createdAt}
          >
            {getFormattedDate(comment.createdAt)}
          </time>
        </div>

        {showMenu && (
          <div className="publication-comment__menu">
            <button
              type="button"
              className="publication-comment__menu-toggle"
              aria-label="Comment options"
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>

            {isMenuOpen && (
              <div className="publication-comment__menu-dropdown" role="menu">
                {canEdit && (
                  <button
                    type="button"
                    role="menuitem"
                    className="publication-comment__menu-option"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsEditing(true);
                    }}
                  >
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    role="menuitem"
                    className="publication-comment__menu-option publication-comment__menu-option--danger"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </header>

      {isEditing ? (
        <div className="publication-comment__edit">
          <textarea
            className="publication-comment__edit-input"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Edit comment"
          />
          <div className="publication-comment__edit-actions">
            <button
              type="button"
              className="link-btn"
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
            <button type="button" className="btn" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <p className="publication-comment__body">{comment.text}</p>
      )}
    </article>
  );
};

export default CommentItem;
